// utils/gridLayout.ts
import type { Node, Edge } from "@xyflow/react";

const CHILDREN_PER_ROW = 5;
const NODE_HORIZONTAL_SPACING = 60;
const NODE_VERTICAL_SPACING = 120;
const ROW_VERTICAL_SPACING = 80;

interface LayoutOptions {
  nodeWidth?: number;
  nodeHeight?: number;
  horizontalSpacing?: number;
  verticalSpacing?: number;
  rowSpacing?: number;
}

interface SubtreeBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Applies a grid-based layout to tree nodes where siblings are arranged in rows
 * @param nodes - ReactFlow nodes
 * @param edges - ReactFlow edges
 * @param options - Layout configuration options
 * @returns Nodes with updated positions
 */
export function applyGridLayout(
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
) {
  const {
    nodeWidth = 220,
    nodeHeight = 72,
    horizontalSpacing = NODE_HORIZONTAL_SPACING,
    verticalSpacing = NODE_VERTICAL_SPACING,
    rowSpacing = ROW_VERTICAL_SPACING,
  } = options;

  // Build parent-child relationship map
  const childrenByParent = new Map<string, string[]>();
  const parentByChild = new Map<string, string>();
  const nodeMap = new Map<string, Node>();

  // Build lookup maps
  nodes.forEach((node) => {
    nodeMap.set(node.id, node);
  });

  edges.forEach((edge) => {
    const children = childrenByParent.get(edge.source) || [];
    children.push(edge.target);
    childrenByParent.set(edge.source, children);
    parentByChild.set(edge.target, edge.source);
  });

  // Find root nodes (nodes without parents)
  const rootNodes: string[] = [];
  nodes.forEach((node) => {
    if (!parentByChild.has(node.id)) {
      rootNodes.push(node.id);
    }
  });

  const positions = new Map<string, { x: number; y: number }>();
  const subtreeBounds = new Map<string, SubtreeBounds>();

  /**
   * Position a node and its descendants, return the bounding box
   */
  const positionSubtree = (nodeId: string, startX: number, startY: number): SubtreeBounds => {
    const node = nodeMap.get(nodeId);
    if (!node) {
      return { minX: startX, maxX: startX, minY: startY, maxY: startY };
    }

    const width = (node.width ?? node.data?.width ?? nodeWidth) as number;
    const height = (node.height ?? node.data?.height ?? nodeHeight) as number;

    // Position this node
    positions.set(nodeId, { x: startX, y: startY });

    const childIds = childrenByParent.get(nodeId) || [];

    if (childIds.length === 0) {
      // Leaf node - return its bounds
      return {
        minX: startX,
        maxX: startX + width,
        minY: startY,
        maxY: startY + height,
      };
    }

    // Position children in a grid below this node
    const childrenStartY = startY + height + verticalSpacing;
    let currentRowStartX = startX;
    let currentY = childrenStartY;
    let maxX = startX + width; // Track the rightmost edge
    let maxY = startY + height; // Track the bottommost edge
    let currentRowMaxY = childrenStartY; // Track the bottom of the current row

    childIds.forEach((childId, index) => {
      const col = index % CHILDREN_PER_ROW;
      const row = Math.floor(index / CHILDREN_PER_ROW);

      // Start a new row - move below the tallest subtree in the previous row
      if (col === 0 && index > 0) {
        currentRowStartX = startX;
        currentY = currentRowMaxY + rowSpacing;
        currentRowMaxY = currentY; // Reset for new row
      }

      // Position this child and its subtree
      const childBounds = positionSubtree(childId, currentRowStartX, currentY);

      // Update our bounds
      maxX = Math.max(maxX, childBounds.maxX);
      maxY = Math.max(maxY, childBounds.maxY);

      // Track the tallest point in this row (including all descendants)
      currentRowMaxY = Math.max(currentRowMaxY, childBounds.maxY);

      // Calculate the width this child's subtree actually used
      const childSubtreeWidth = childBounds.maxX - currentRowStartX;

      // Move to the next column position
      // Use the actual subtree width, not just the node width
      currentRowStartX = currentRowStartX + childSubtreeWidth + horizontalSpacing;
    });

    const bounds = {
      minX: startX,
      maxX: maxX,
      minY: startY,
      maxY: maxY,
    };

    subtreeBounds.set(nodeId, bounds);
    return bounds;
  };

  // Position each root tree with proper spacing
  let currentX = 0;

  rootNodes.forEach((rootId) => {
    const bounds = positionSubtree(rootId, currentX, 0);

    // Move to the right edge of this subtree plus spacing for the next tree
    currentX = bounds.maxX + horizontalSpacing * 3;
  });

  // Apply positions to nodes
  const layoutedNodes: Node[] = nodes.map((node) => {
    const pos = positions.get(node.id) ?? { x: 0, y: 0 };
    return {
      ...node,
      position: pos,
      draggable: true,
    };
  });

  return { nodes: layoutedNodes, edges };
}

